<script lang="ts">import { Dropdown } from "@infinitro/dropdown";
import Input from "./input.svelte";
import { buildCountryOptions, formatMatchedRegionLabel, getDefaultCountryOption, normalizeNationalNumber, validatePhoneAgainstConfig } from "./phone-utils";
let { id = "phone-input", config = [], telCountryCode = $bindable(""), nationalNumber = $bindable(""), country = $bindable(""), valid = $bindable(false), matchedCountryName = $bindable(""), placeholder = "", disabled = false, loading = false, showError = false, errorMessage = "", size = "large", customClasses = "", inputClasses = "", customBorderRadius = undefined, prefixDropdownClasses = "phone-prefix-dropdown", prefixDropdownInputClasses = "", onInput, onBlur } = $props();
let selectedOption = $state("");
// Options are keyed on the dial code only (e.g. value `1`, label `+1`).
const countryOptions = $derived(buildCountryOptions(config));
const prefixPlaceholder = $derived(countryOptions[0]?.label ?? "+");
$effect.pre(() => {
	if (config.length === 0) return;
	const optionValues = new Set(countryOptions.map((option) => option.value));
	const stillValid = selectedOption && optionValues.has(selectedOption);
	if (!stillValid) {
		selectedOption = telCountryCode && optionValues.has(String(telCountryCode)) ? String(telCountryCode) : getDefaultCountryOption(config);
	}
	if (selectedOption && telCountryCode !== selectedOption) {
		telCountryCode = selectedOption;
	}
});
const validation = $derived(config.length > 0 && telCountryCode ? validatePhoneAgainstConfig(config, telCountryCode, nationalNumber) : {
	valid: false,
	e164: "",
	nationalDigits: "",
	matchedConfig: null,
	ambiguous: false
});
// The dropdown carries the dial code only; country/region resolves from the typed number and is
// shown as a secondary hint only when a single country matches unambiguously.
const regionHint = $derived(validation.matchedConfig ? formatMatchedRegionLabel(validation.matchedConfig) : "");
const showRegionHint = $derived(nationalNumber.trim().length > 0 && regionHint !== "");
// Keep bindable outputs in sync immediately (avoid stale true after shortening a number).
$effect.pre(() => {
	valid = validation.valid;
	country = validation.matchedConfig?.country ?? "";
	matchedCountryName = regionHint;
});
const handleCountryChange = (value) => {
	selectedOption = value;
	telCountryCode = value;
	onInput?.();
};
const handleNationalInput = (value) => {
	nationalNumber = normalizeNationalNumber(value);
	onInput?.();
};
</script>

<div class="phone-input-row" class:phone-input-disabled={disabled || loading}>
	<div class="phone-prefix-field">
		<Dropdown
			bind:value={selectedOption}
			options={countryOptions}
			placeholder={prefixPlaceholder}
			{size}
			customClasses="phone-input-prefix {prefixDropdownClasses} {customClasses}"
			inputClasses={prefixDropdownInputClasses || inputClasses}
			enableSearch={countryOptions.length > 1}
			menuPosition="bottom"
			width="100%"
			portalMenu={true}
			menuClasses="phone-prefix-menu"
			disabled={disabled || loading || countryOptions.length === 0}
			onChange={handleCountryChange}
		/>
	</div>
	<div class="phone-number-field">
		<Input
			{id}
			bind:value={nationalNumber}
			{placeholder}
			inputmode="numeric"
			pattern="[0-9]*"
			error={showError}
			message={showError ? errorMessage : ''}
			{size}
			{customClasses}
			{inputClasses}
			{customBorderRadius}
			disabled={disabled || loading}
			onInput={handleNationalInput}
			{onBlur}
		/>
	</div>
</div>
{#if showRegionHint}
	<span class="phone-region-hint">{regionHint}</span>
{/if}

<style>
	.phone-input-row {
		display: flex;
		width: 100%;
		min-width: 0px;
		align-items: flex-start;
		gap: 0.5rem;
}

	.phone-prefix-field {
		position: relative;
		z-index: 50;
		width: 9rem;
		min-width: 8rem;
		max-width: 40%;
		flex-shrink: 0;
}

	.phone-number-field {
		min-width: 0px;
		flex: 1 1 0%;
}

	.phone-input-disabled {
		opacity: 0.7;
}

	.phone-region-hint {
		margin-top: 0.25rem;
		display: block;
		font-size: 0.75rem;
		line-height: 1rem;
		opacity: 0.7;
}

	:global(.phone-input-prefix) {
		width: 100% !important;
		min-width: 0px !important;
		max-width: 100% !important;
}

	:global(.phone-input-prefix .input > span) {
		display: block;
		min-width: 0px;
		flex: 1 1 0%;
		overflow: hidden;
		text-overflow: ellipsis;
		white-space: nowrap;
}

	/* The prefix menu is portaled to <body> (portalMenu) — target it by its own class, not by an
	   ancestor, so the overrides still match. Dial-code labels are short, so no widening needed;
	   just force opaque tokens (background-500 is translucent on some brands, e.g. turboslots). */
	:global(.phone-prefix-menu.options-list),
	:global(.phone-prefix-menu.options-list.options-list-blur),
	:global(.phone-prefix-menu.options-list.options-list-alternative2-glass) {
		background: var(--color-background-100) !important;
		background-image: none !important;
		backdrop-filter: none !important;
		-webkit-backdrop-filter: none !important;
		opacity: 1 !important;
	}

	:global(.phone-prefix-menu .search-input) {
		background: var(--color-background-300) !important;
		background-image: none !important;
		backdrop-filter: none !important;
		-webkit-backdrop-filter: none !important;
		opacity: 1 !important;
	}

	:global(.phone-prefix-menu .search-container),
	:global(.phone-prefix-menu .search-container-blur) {
		background: transparent !important;
		backdrop-filter: none !important;
		-webkit-backdrop-filter: none !important;
	}
</style>
